/**
 * KOM IGÅNG — de tre frågorna som måste besvaras innan en enda timme går att
 * rapportera, ställda en i taget, på en skärm.
 *
 * VARFÖR DEN FINNS. Appen byggdes för en firma som redan är igång: hela specen,
 * de nitton besluten och prototypen utgår från att det finns jobb, folk och
 * priser. Ingen ritade dagen man börjar. Vägen från "Lägg upp firman" till en
 * sparad timme var därför ELVA skärmar och ett par och tjugo tryck —
 * Inställningar -> Moment -> tillbaka -> Inställningar -> Timpriser -> tillbaka
 * -> Arbetsordrar -> Ny -> tillbaka -> Tidrapport -> Ny -> Spara — och de tre
 * nödvändiga stegen låg bakom ett kugghjul som ingenting pekade mot. Den här
 * skärmen är samma tre skrivningar i tre frågor.
 *
 * DEN ÄGER INGEN EGEN DATA OCH INGEN EGEN SKRIVVÄG. Varje steg använder det
 * lager och de op-byggare skärmen för just den saken redan har
 * (timpriser-data.ts, moment-data.ts, arbetsorder-data.ts). Ett fjärde ställe
 * som skapar moment hade varit ett fjärde ställe att hålla i takt med
 * kontraktet.
 *
 * STEGEN RÄKNAS FRAM UR VAD SOM SAKNAS, aldrig ur en räknare som står i
 * skärmen. Den som hoppade av efter timpriset och kommer tillbaka börjar på
 * momenten; den som redan har moment får två steg och inte tre. `kravenFor()` i
 * oversikt.ts är samma källa som startsidans kort räknar ur, så kortet och
 * skärmen kan inte säga olika saker.
 *
 * VARJE STEG SPARAS FÖR SIG, och det är inte en förenkling. Ett enda kuvert i
 * slutet hade varit färre anrop men gjort avhoppet dyrt: tappat nät i steg tre
 * och timpriset är också borta. Nu är varje besvarad fråga sparad i samma sekund
 * den besvaras.
 *
 * OP_ID FRYSES MED HELA KROPPEN VID FÖRSTA TRYCKET (beslut 09), precis som i
 * NyOrder och NyFirma: ett omtag skickar SAMMA op_id med SAMMA kropp och spelas
 * upp ur op-loggen i stället för att lägga upp saken två gånger. Planen släpps
 * bara när servern SVARAT och avvisat — efter ett nätfel ligger den kvar, för då
 * vet vi inte om anropet gick fram.
 *
 * "GÖR DET SEN" FINNS PÅ VARJE STEG, med flit. Skärmen är en genväg och inte en
 * grind: den som hellre gräver i Inställningar ska få göra det, och den som
 * öppnade appen för att titta ska kunna backa ut. Vad som saknas står kvar på
 * startsidan tills det är gjort.
 */

import { useMemo, useState } from 'react';
import { REPORTING_ROLES } from '@delat/routes';
import type {
  CustomerId,
  CustomerKind,
  OpId,
  PriceRole,
  SyncOp,
  WorkOrderId,
} from '@delat/types';
import {
  Bricka,
  FAKTOR,
  Falt,
  Felruta,
  Knapp,
  Kryssrad,
  Laddar,
  Overrubrik,
  Rubrik,
  Talfalt,
  Tomtext,
  Valfalt,
  Yta,
} from '../ui';
import { granska } from '../api';
import type { Klartidfel } from '../api';
import { useInloggad } from '../app/session';
import { useNavigering } from '../app/navigering';
import { glomAllaFirmaminnen } from '../app/firmaminne';
import { kravenFor, useOversikt } from './oversikt';
import type { Krav, Kravslag } from './oversikt';
import {
  MAX_ORE_PER_TIMME,
  nyttPrisOp,
  prisForRoll,
  useTimprislager,
} from './timpriser-data';
import type { Prislager } from './timpriser-data';
import {
  MAX_MOMENTNAMN_TECKEN,
  nyttMomentId,
  nyttMomentOp,
  nyttOpId,
} from './moment-data';
import {
  arKlar,
  forstaMisslyckade,
  nyKundOp,
  nu,
  nyttKundId,
  nyttOrderId,
  skickaOps,
} from './arbetsorder-data';

/* == Momentförslagen ======================================================
   SEX FÖRSLAG, TRE FÖRKRYSSADE, och `rot` på var och en är inte kosmetika:
   TaskWrite bär rot_eligible, och satsen fryses på varje tidrad som skrivs på
   momentet. Ett moment som felaktigt ger ROT blir en avdragspost kunden inte
   har rätt till; ett som felaktigt INTE ger det är pengar kunden aldrig får
   tillbaka. Restid och materialhämtning är inte arbete i bostaden och ger
   därför ingen ROT — samma fälla som en gång delade en arbetsdag 4 + 4 mellan
   resa och arbete och tappade halva ROT-underlaget utan att någon sa något.

   ORDEN ÄR KUNDENS. De hamnar ordagrant på fakturan, så de är skrivna för att
   läsas av den som ska betala — inte som interna kategorier. Chefen byter och
   lägger till fritt i Inställningar -> Moment efteråt; de här sex är en start
   som duger för VVS, bygg, el och måleri.
   ======================================================================= */

interface Momentforslag {
  readonly namn: string;
  readonly rot: boolean;
  readonly forkryssad: boolean;
}

const MOMENTFORSLAG: readonly Momentforslag[] = [
  { namn: 'Arbete på plats', rot: true, forkryssad: true },
  { namn: 'Felsökning', rot: true, forkryssad: true },
  { namn: 'Service och underhåll', rot: true, forkryssad: true },
  { namn: 'Installation', rot: true, forkryssad: false },
  { namn: 'Rivning och bilning', rot: true, forkryssad: false },
  // INGEN ROT. Restid och hämtning är inte arbete i bostaden.
  { namn: 'Resa och materialhämtning', rot: false, forkryssad: false },
];

/* == Skärmen ============================================================== */

export const Igang = () => {
  const { firmaId, arChef } = useInloggad();
  const { ga, ersatt } = useNavigering();
  const oversikt = useOversikt();
  const pris = useTimprislager(firmaId);

  /*
    BESKED I STÄLLET FÖR FÄLT ÅT DEN SOM INTE FÅR SKRIVA (beslut 07). Skärmen
    ligger i läge 'lag' för att varje steg skriver på en MANAGER_ONLY-rutt — en
    montör har den alltså inte i sin skärmkarta alls. Bokföraren HAR full vy och
    når adressen, och han ska mötas av ett svar och inte av tre formulär som
    säger nej ett i taget. Han får heller aldrig kortet på startsidan: kravenFor
    frågar om samma sak.
  */
  if (!arChef) {
    return (
      <>
        <Rubrik titel="Kom igång" under="Det här är chefens uppgifter" />
        <Tomtext>
          Timpriser, moment och arbetsordrar läggs upp av den som driver firman.
          Du ser allt som redan finns, men det är inte du som ska fylla i det
          här.
        </Tomtext>
        <Knapp variant="stor-tyst" onClick={() => ga('hem')}>
          Till startsidan
        </Knapp>
      </>
    );
  }

  if (oversikt.slag === 'fel') return <Felruta fel={oversikt.fel} />;
  if (pris.lage.status === 'fel') {
    return (
      <Felruta
        fel={pris.lage.fel}
        atgard={
          <Knapp variant="piller-mini" onClick={pris.laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }
  if (oversikt.slag === 'laddar' || pris.lage.status === 'laddar') {
    return <Laddar rader={5} />;
  }

  const krav = kravenFor(oversikt.oversikt, true);

  /*
    ALLT ÄR REDAN GJORT. Skärmen nås från kortet på startsidan, men också från
    en bokmärkt adress och från det andra tryck man hann göra innan pullen kom
    tillbaka. Då ska den säga att det är klart — inte visa ett tomt steg 0 av 0.
  */
  if (krav.length === 0) {
    return (
      <>
        <Rubrik titel="Allt klart" under="Firman är igång" />
        <Tomtext>
          Timpris, moment och ett jobb finns. Skriv in tiden när jobbet är
          gjort — det är hela appen.
        </Tomtext>
        <Knapp onClick={() => ersatt('hem')}>Till startsidan</Knapp>
      </>
    );
  }

  return <Steg krav={krav} prislager={pris.lage.lager} laddaOmPris={pris.laddaOm} />;
};

/* == Stegen =============================================================== */

/**
 * Ett steg åt gången ur `krav`, som redan står i den ordning sakerna blockerar
 * varandra: pris, moment, jobb. Skärmen räknar INTE om listan mellan stegen —
 * den hämtades en gång, och ett steg som försvann mitt i hade flyttat marken
 * under den som skriver. Räkningen "Steg 2 av 3" är därför sann hela vägen.
 */
const Steg = ({
  krav,
  prislager,
  laddaOmPris,
}: {
  readonly krav: readonly Krav[];
  readonly prislager: Prislager;
  readonly laddaOmPris: () => void;
}) => {
  const { ersatt } = useNavigering();
  const [gjorda, sattGjorda] = useState<readonly Kravslag[]>([]);

  const kvar = krav.filter((k) => !gjorda.includes(k.slag));
  const nuvarande = kvar[0];
  const nummer = krav.length - kvar.length + 1;

  /** Ett steg är klart: lagren är inaktuella, och nästa steg ska läsa nytt. */
  const klar = (slag: Kravslag): void => {
    glomAllaFirmaminnen();
    sattGjorda((f) => [...f, slag]);
  };

  if (nuvarande === undefined) {
    return (
      <>
        <Overrubrik>Klart</Overrubrik>
        <Rubrik titel="Firman är igång" under="Nu går det att rapportera tid" />
        <Tomtext>
          Timpriset, momenten och jobbet ligger i firman. Allt går att ändra i
          Inställningar — och nästa jobb lägger du upp under Arbetsordrar.
        </Tomtext>
        <Knapp onClick={() => ersatt('hem')}>Till startsidan</Knapp>
      </>
    );
  }

  return (
    <>
      <Overrubrik>
        Steg {nummer} av {krav.length}
      </Overrubrik>
      <Rubrik titel={nuvarande.rubrik} under={nuvarande.under} />

      {nuvarande.slag === 'timpris' && (
        <Timprissteg
          prislager={prislager}
          laddaOm={laddaOmPris}
          onKlar={() => klar('timpris')}
        />
      )}
      {nuvarande.slag === 'moment' && <Momentsteg onKlar={() => klar('moment')} />}
      {nuvarande.slag === 'jobb' && <Jobbsteg onKlar={() => klar('jobb')} />}

      {/* GENVÄGEN ÄR INTE EN GRIND. Vad som saknas står kvar på startsidan. */}
      <Knapp variant="stor-tyst" onClick={() => ersatt('hem')}>
        Gör det sen
      </Knapp>
    </>
  );
};

/* == Steg: timpriset ======================================================
   ETT FÄLT, FYRA ROLLER. Chefen frågas om ETT tal — vad firman tar per
   timme — och samma tal sätts på alla fyra rapporterande roller
   (REPORTING_ROLES). Skälet är att alternativet är sämre på båda sätten: fyra
   fält på dag ett är tre fält han inte kan svara på, och ett pris bara på hans
   egen roll betyder att FÖRSTA montören som rapporterar möts av PRICE_MISSING
   från servern — ett fel han inte kan rätta själv.

   BARA DE ROLLER SOM SAKNAR PRIS FÅR EN RAD. Ett nytt pris på en roll som redan
   har ett ger ROW_DUPLICATE, och det är rätt svar från servern; skärmen ska
   bara inte fråga efter det.

   PRISLISTAN ÄR FIRMANS STANDARDLISTA och myntas inte här. Den föddes med
   firman (CREATE_COMPANY bär default_price_list_id) och är tom tills nu.
   ======================================================================= */

const Timprissteg = ({
  prislager,
  laddaOm,
  onKlar,
}: {
  readonly prislager: Prislager;
  readonly laddaOm: () => void;
  readonly onKlar: () => void;
}) => {
  const { firmaId } = useInloggad();
  const [text, sattText] = useState('');
  const [oren, sattOren] = useState<number | null>(null);
  const [plan, sattPlan] = useState<readonly SyncOp[] | null>(null);
  const [klara, sattKlara] = useState<readonly OpId[]>([]);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<string | null>(null);

  const utan = REPORTING_ROLES.filter((roll) => prisForRoll(prislager, roll) === null);

  /*
    PRISLISTAN SAKNAS I SVARET. Det betyder antingen att vyn inte var 'full' —
    men hit kommer bara chefen — eller att något är fel i firman. Skärmen säger
    det rakt ut i stället för att mynta en andra standardlista, vilket hade gett
    firman två och gjort prisupplösningen godtycklig.
  */
  if (prislager.prislista === null) {
    return (
      <>
        <Tomtext>
          Firmans prislista kom inte med i svaret, så timpriset går inte att
          spara just nu. Hämta om — håller det i sig behöver firman ses över.
        </Tomtext>
        <Knapp variant="stor-tyst" onClick={laddaOm}>
          Hämta om
        </Knapp>
      </>
    );
  }
  const listaId = prislager.prislista.id;

  const byggPlan = (): readonly SyncOp[] | null => {
    if (oren === null || oren < 1) {
      sattFaltfel('Skriv timpriset i kronor, till exempel 650.');
      return null;
    }
    if (oren > MAX_ORE_PER_TIMME) {
      sattFaltfel('Det ser för högt ut. Högst 100 000 kr per timme.');
      return null;
    }
    sattFaltfel(null);
    return utan.map(
      (roll: PriceRole) =>
        nyttPrisOp(listaId, roll, granska.orePerTimme(oren, 'ore_per_hour')).op,
    );
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    if (plan === null) sattPlan(aktuell);

    const attSkicka = aktuell.filter((op) => !klara.includes(op.op_id));
    if (attSkicka.length === 0) {
      onKlar();
      return;
    }

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, attSkicka);
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      const nyaKlara = [...klara];
      for (const op of attSkicka) if (arKlar(svar.varde, op.op_id)) nyaKlara.push(op.op_id);
      sattKlara(nyaKlara);
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        // SERVERN SVARADE OCH SA NEJ -> ingen rad blev till, fälten öppnas igen.
        sattPlan(null);
        sattFel(opsfel);
        return;
      }
      onKlar();
    })();
  };

  return (
    <>
      <Yta>
        <Talfalt
          etikett="Vad tar du per timme?"
          varde={text}
          faktor={FAKTOR.kronor_oren}
          suffix="kr"
          disabled={plan !== null && sparar}
          autoFocus
          onAndra={(v, heltal) => {
            sattText(v);
            sattOren(heltal);
            sattFaltfel(null);
            sattFel(null);
          }}
          hjalp="Ex moms. Priset fryses på varje tidrad när den sparas — ändrar du det senare står gamla rader kvar."
          {...(faltfel === null ? {} : { fel: faltfel })}
        />
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <Knapp disabled={sparar} onClick={spara}>
        {sparar ? 'Sparar ...' : plan === null ? 'Nästa' : 'Försök igen'}
      </Knapp>

      <Tomtext>
        Samma pris sätts på chef, montör, lärling och inhyrd — då kan alla
        rapportera från dag ett. Ska de skilja sig ändrar du dem under
        Inställningar &rarr; Timpriser.
      </Tomtext>
    </>
  );
};

/* == Steg: momenten ======================================================= */

const Momentsteg = ({ onKlar }: { readonly onKlar: () => void }) => {
  const { firmaId } = useInloggad();
  const [valda, sattValda] = useState<readonly string[]>(() =>
    MOMENTFORSLAG.filter((m) => m.forkryssad).map((m) => m.namn),
  );
  const [eget, sattEget] = useState('');
  const [plan, sattPlan] = useState<readonly SyncOp[] | null>(null);
  const [klara, sattKlara] = useState<readonly OpId[]>([]);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<string | null>(null);

  const last = plan !== null;

  /** Egna moment skrivna med komma emellan blir flera rader, inte en lång. */
  const egnaNamn = useMemo(
    () =>
      eget
        .split(',')
        .map((d) => d.trim())
        .filter((d) => d !== ''),
    [eget],
  );

  const antal = valda.length + egnaNamn.length;

  const byggPlan = (): readonly SyncOp[] | null => {
    if (antal === 0) {
      sattFaltfel('Kryssa i minst ett moment, eller skriv ett eget.');
      return null;
    }
    const forLangt = egnaNamn.find((n) => n.length > MAX_MOMENTNAMN_TECKEN);
    if (forLangt !== undefined) {
      sattFaltfel(`"${forLangt.slice(0, 20)} ..." är för långt. Högst ${String(MAX_MOMENTNAMN_TECKEN)} tecken.`);
      return null;
    }
    // Samma namn två gånger är ingen faktura kunden begriper — och listan är
    // tom före det här steget, så det räcker att pröva inom det man skrivit.
    const alla = [...valda, ...egnaNamn];
    const gemena = alla.map((n) => n.toLowerCase());
    const dubblett = alla.find((n, i) => gemena.indexOf(n.toLowerCase()) !== i);
    if (dubblett !== undefined) {
      sattFaltfel(`"${dubblett}" står två gånger.`);
      return null;
    }
    sattFaltfel(null);

    const ops: SyncOp[] = [];
    for (const namn of valda) {
      const forslag = MOMENTFORSLAG.find((m) => m.namn === namn);
      ops.push(
        nyttMomentOp(nyttMomentId(), namn, forslag?.rot ?? true, nyttOpId()),
      );
    }
    // EGNA MOMENT GER ROT. De skrivs av någon som beskriver ett arbete han
    // utför, och arbete i bostaden är det ROT gäller; undantagen är resa och
    // material, som redan står som förslag. Fel gissning rättas på en rad i
    // Inställningar -> Moment, och den skärmen visar satsen per moment.
    for (const namn of egnaNamn) {
      ops.push(nyttMomentOp(nyttMomentId(), namn, true, nyttOpId()));
    }
    return ops;
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    if (plan === null) sattPlan(aktuell);

    const attSkicka = aktuell.filter((op) => !klara.includes(op.op_id));
    if (attSkicka.length === 0) {
      onKlar();
      return;
    }

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, attSkicka);
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      const nyaKlara = [...klara];
      for (const op of attSkicka) if (arKlar(svar.varde, op.op_id)) nyaKlara.push(op.op_id);
      sattKlara(nyaKlara);
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        sattPlan(null);
        sattFel(opsfel);
        return;
      }
      onKlar();
    })();
  };

  return (
    <>
      <Yta rubrik="Vad gör du oftast?" rakning={antal === 0 ? undefined : `${String(antal)} valda`}>
        {MOMENTFORSLAG.map((m) => (
          <Kryssrad
            key={m.namn}
            form="moment"
            ikryssad={valda.includes(m.namn)}
            disabled={last}
            varde={m.rot ? undefined : <Bricka ton="neutral">ingen ROT</Bricka>}
            onToggle={() => {
              sattFaltfel(null);
              sattValda((f) =>
                f.includes(m.namn) ? f.filter((n) => n !== m.namn) : [...f, m.namn],
              );
            }}
          >
            {m.namn}
          </Kryssrad>
        ))}
      </Yta>

      <Yta tyst>
        <Falt
          etikett="Något eget?"
          varde={eget}
          onAndra={(v) => {
            sattEget(v);
            sattFaltfel(null);
          }}
          disabled={last}
          placeholder="Stambyte, plattsättning"
          hjalp="Frivilligt. Flera moment skiljs med komma. Texten hamnar ordagrant på kundens faktura."
          {...(faltfel === null ? {} : { fel: faltfel })}
        />
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <Knapp disabled={sparar} onClick={spara}>
        {sparar
          ? 'Sparar ...'
          : plan === null
            ? antal === 0
              ? 'Välj minst ett'
              : `Spara ${String(antal)} moment`
            : 'Försök igen'}
      </Knapp>

      <Tomtext>
        Momenten kryssas i på varje tidrapport, och det du kryssar i är texten
        kunden läser. Du lägger till och byter fritt under Inställningar &rarr;
        Moment.
      </Tomtext>
    </>
  );
};

/* == Steg: första jobbet ==================================================
   KUND OCH ARBETSORDER I SAMMA KUVERT, samma grepp som NyOrder.tsx: ett
   SYNC_PUSH med två operationer, kundens först. Faller kunden faller ordern med
   den, och det är rätt — en arbetsorder utan kund finns inte i schemat.

   ROT-SATSEN FRÅGAS INTE OM HÄR. Den bor på kunden och fryses på varje tidrad,
   men den är en sak att förstå innan man svarar, och det här är den tredje
   frågan en människa får i appen. Fältet står i Inställningar -> Kunder och i
   NyOrder; kortet på startsidan är borta då och chefen är inne i appen.
   ======================================================================= */

const KUNDSLAG: readonly { readonly varde: CustomerKind; readonly etikett: string }[] = [
  { varde: 'private', etikett: 'Privatperson' },
  { varde: 'company', etikett: 'Företag' },
];

interface Jobbplan {
  readonly kundId: CustomerId;
  readonly kundOp: SyncOp;
  readonly orderId: WorkOrderId;
  readonly orderOp: SyncOp;
}

const Jobbsteg = ({ onKlar }: { readonly onKlar: () => void }) => {
  const { firmaId, konto } = useInloggad();
  const [kundnamn, sattKundnamn] = useState('');
  const [slag, sattSlag] = useState<CustomerKind>('private');
  const [adress, sattAdress] = useState('');
  const [beskrivning, sattBeskrivning] = useState('');
  const [plan, sattPlan] = useState<Jobbplan | null>(null);
  const [klara, sattKlara] = useState<readonly OpId[]>([]);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  const last = plan !== null;

  const byggPlan = (): Jobbplan | null => {
    const brister: Record<string, string> = {};
    const namn = kundnamn.trim();
    const rensadAdress = adress.trim();
    const rensadBeskrivning = beskrivning.trim();

    if (namn === '') brister['kund'] = 'Vem är jobbet för?';
    else if (namn.length > 120) brister['kund'] = 'Högst 120 tecken.';
    if (rensadAdress.length > 200) brister['adress'] = 'Högst 200 tecken.';
    if (rensadBeskrivning === '') brister['jobb'] = 'Skriv vad som ska göras.';
    else if (rensadBeskrivning.length > 2000) brister['jobb'] = 'Högst 2 000 tecken.';

    if (Object.keys(brister).length > 0) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    const kundId = nyttKundId();
    const orderId = nyttOrderId();
    return {
      kundId,
      kundOp: nyKundOp(
        kundId,
        {
          name: namn,
          kind: slag,
          // Org.nr frågas inte om på dag ett; det fylls i under Kunder när
          // fakturan ska ut. CHECK (kind = 'company' OR org_number IS NULL)
          // gör dessutom att en privatperson ALDRIG får ett nummer här.
          org_number: null,
          // Kundens adress är jobbets adress i det vanliga fallet, och det är
          // det här fallet: ett hushåll, ett badrum, en adress.
          address: rensadAdress === '' ? null : rensadAdress,
          price_list_id: null,
          // ROT sätts under Kunder. null = ingen ROT, vilket är det säkra
          // förvalet: en sats som är satt fryses på varje rad.
          rot_hundredths: null,
        },
        nyttOpId(),
      ),
      orderId,
      orderOp: {
        op_id: nyttOpId(),
        client_time: nu(),
        kind: 'put',
        base_version: null,
        table: 'work_order',
        id: orderId,
        row: {
          customer_id: kundId,
          address: rensadAdress === '' ? null : rensadAdress,
          description: rensadBeskrivning,
          status: 'open',
          // DEN SOM LÄGGER UPP FIRMAN STÅR SOM ANSVARIG. Utan det blir jobbet
          // ingens, notissiffran på Arbetsordrar-kaklet står på noll, och den
          // som just skrev in jobbet ser det inte som sitt.
          responsible_account_id: konto.id,
          price_list_id: null,
        },
      },
    };
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    if (plan === null) sattPlan(aktuell);

    const attSkicka = [aktuell.kundOp, aktuell.orderOp].filter(
      (op) => !klara.includes(op.op_id),
    );
    if (attSkicka.length === 0) {
      onKlar();
      return;
    }

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, attSkicka);
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      const nyaKlara = [...klara];
      for (const op of attSkicka) if (arKlar(svar.varde, op.op_id)) nyaKlara.push(op.op_id);
      sattKlara(nyaKlara);
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        sattPlan(null);
        sattFel(opsfel);
        return;
      }
      onKlar();
    })();
  };

  return (
    <>
      <Yta rubrik="Jobbet du är på nu">
        <Falt
          etikett="Vem är det för?"
          varde={kundnamn}
          onAndra={(v) => {
            sattKundnamn(v);
            sattFel(null);
          }}
          disabled={last}
          autoFocus
          placeholder="Familjen Ek"
          {...(faltfel['kund'] === undefined ? {} : { fel: faltfel['kund'] })}
        />
        <Valfalt
          etikett="Privat eller företag?"
          varde={slag}
          onAndra={(v) => sattSlag(v === 'company' ? 'company' : 'private')}
          val={KUNDSLAG}
          disabled={last}
          hjalp="Privatperson kan få ROT. Satsen sätter du under Kunder."
        />
        <Falt
          etikett="Adress"
          varde={adress}
          onAndra={sattAdress}
          disabled={last}
          autoComplete="street-address"
          placeholder="Skolvägen 12"
          hjalp="Frivilligt, men det är adressen du känner igen jobbet på i listan."
          {...(faltfel['adress'] === undefined ? {} : { fel: faltfel['adress'] })}
        />
        <Falt
          etikett="Vad ska göras?"
          varde={beskrivning}
          onAndra={(v) => {
            sattBeskrivning(v);
            sattFel(null);
          }}
          disabled={last}
          placeholder="Byta blandare och rensa avlopp"
          {...(faltfel['jobb'] === undefined ? {} : { fel: faltfel['jobb'] })}
        />
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <Knapp disabled={sparar} onClick={spara}>
        {sparar ? 'Sparar ...' : plan === null ? 'Lägg upp jobbet' : 'Försök igen'}
      </Knapp>

      {last && !sparar && fel !== null && fel.slag === 'nat' && (
        <Tomtext>
          Kom inget svar? Tryck Försök igen — sparningen bär samma id, så jobbet
          kan inte bli två.
        </Tomtext>
      )}

      <Tomtext>
        Du står som ansvarig för jobbet. Nästa jobb lägger du upp under
        Arbetsordrar, och tiden rapporteras alltid på ett jobb.
      </Tomtext>
    </>
  );
};
